import React, { useState } from 'react';
import { testimonials } from '../data/testimonials';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import TestimonialCard from './ui/TestimonialCard';

/**
 * Componente de carrossel para exibir depoimentos de clientes
 * Layout igual à imagem de referência com fundo azul escuro
 * Chamado por: CaseSection
 * Chama: TestimonialCard, dados de testimonials.ts
 * Complexidade: O(1) para navegação, O(1) para renderização (apenas um card por vez)
 */
const TestimonialsCarousel: React.FC = () => {
  // Estado para controlar qual depoimento está ativo
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Estados para controle de touch/swipe
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  /**
   * Navega para um depoimento específico pelo índice
   * Complexidade: O(1)
   */
  const goToSlide = (index: number) => {
    if (index === currentIndex || isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentIndex(index);
    
    // Remove o estado de transição após a animação
    setTimeout(() => setIsTransitioning(false), 200);
  };

  /**
   * Navega para o próximo depoimento
   * Complexidade: O(1)
   */
  const nextSlide = () => {
    const nextIndex = (currentIndex + 1) % testimonials.length;
    goToSlide(nextIndex);
  };

  /**
   * Navega para o depoimento anterior
   * Complexidade: O(1)
   */
  const prevSlide = () => {
    const prevIndex = currentIndex === 0 ? testimonials.length - 1 : currentIndex - 1;
    goToSlide(prevIndex);
  };

  /**
   * Handlers para swipe em dispositivos touch
   * Complexidade: O(1)
   */
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      nextSlide(); // Swipe para esquerda = próximo slide
    } else if (isRightSwipe) {
      prevSlide(); // Swipe para direita = slide anterior
    }
  };

  // Depoimento atual para renderizar
  const currentTestimonial = testimonials[currentIndex];

  return (
    <div 
      id="case-sucesso"
      className="w-full relative bg-primary py-12 md:py-16"
      style={{ backgroundColor: '#003366' }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="max-w-6xl mx-auto px-4">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden relative">
          
          {/* Setas de navegação */}
          <button
            onClick={prevSlide}
            disabled={isTransitioning}
            className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-primary rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 disabled:opacity-50"
            aria-label="Depoimento anterior"
          >
            <FaChevronLeft className="w-5 h-5" />
          </button>
          
          <button
            onClick={nextSlide}
            disabled={isTransitioning}
            className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 bg-white/90 hover:bg-white text-primary rounded-full p-3 shadow-lg transition-all duration-200 hover:scale-110 disabled:opacity-50"
            aria-label="Próximo depoimento"
          >
            <FaChevronRight className="w-5 h-5" />
          </button>

          <div className="relative overflow-hidden h-[780px] lg:h-[680px]">
            {/* Container do card atual com transição */}
            <div 
              className={`h-full transition-opacity duration-300 ease-in-out ${
                isTransitioning ? 'opacity-0' : 'opacity-100'
              }`}
            >
              <TestimonialCard testimonial={currentTestimonial} />
            </div>
          </div>
        </div>
        
        {/* Navegação por pontos - movida para fora do card */}
        <div className="flex justify-center mt-6">
          <div className="flex space-x-3">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                disabled={isTransitioning}
                className={`w-4 h-4 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-secondary shadow-lg transform scale-125' 
                    : 'bg-white/60 hover:bg-white/80 hover:scale-110'
                } disabled:opacity-50`}
                aria-label={`Ir para depoimento ${index + 1}`}
              />
            ))}
          </div>
        </div>
        
        {/* CTA Centralizado */}
        <div className="text-center mt-10">
          <h3 className="text-xl md:text-2xl font-bold text-white mb-6">
            Pronto para ter resultados como estes?
          </h3>
          <button
            onClick={() => {
              const hero = document.getElementById('hero');
              if (hero) hero.scrollIntoView({ behavior: 'smooth' });
            }}
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-yellow-400 to-yellow-500 text-black font-bold text-lg rounded-lg shadow-lg hover:from-yellow-500 hover:to-yellow-600 transform hover:scale-105 transition-all duration-300"
          >
            QUERO COMEÇAR AGORA
            <svg 
              className="ml-2 w-5 h-5" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestimonialsCarousel; 